import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImageCropModal } from '../../components/media/ImageCropModal';
import {
  cropImageToFile,
  readFileAsDataUrl,
  type ImageCropSpec,
} from '../media/imageCrop';

type Resolver = (file: File) => void;
type Rejecter = (reason?: unknown) => void;

interface CropModalState {
  open: boolean;
  imageSrc: string;
  originalFileName: string;
  spec: ImageCropSpec;
}

export function useImageCropModal() {
  const [state, setState] = useState<CropModalState | null>(null);
  const [processing, setProcessing] = useState(false);
  const resolveRef = useRef<Resolver | null>(null);
  const rejectRef = useRef<Rejecter | null>(null);

  const clear = useCallback(() => {
    setState(null);
    setProcessing(false);
    resolveRef.current = null;
    rejectRef.current = null;
  }, []);

  const openCrop = useCallback(async (file: File, spec: ImageCropSpec): Promise<File> => {
    const imageSrc = await readFileAsDataUrl(file);
    return new Promise<File>((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
      setState({
        open: true,
        imageSrc,
        originalFileName: file.name || 'image.jpg',
        spec,
      });
    });
  }, []);

  const cancelCrop = useCallback(() => {
    rejectRef.current?.(new Error('Beskjæring avbrutt av bruker.'));
    clear();
  }, [clear]);

  const confirmCrop = useCallback(
    async (croppedAreaPixels: { x: number; y: number; width: number; height: number }) => {
      if (!state) return;
      setProcessing(true);
      try {
        const croppedFile = await cropImageToFile(
          state.imageSrc,
          croppedAreaPixels,
          state.originalFileName,
          state.spec
        );
        resolveRef.current?.(croppedFile);
        clear();
      } catch (error) {
        rejectRef.current?.(error);
        clear();
      }
    },
    [state, clear]
  );

  const cropModal = state
    ? createPortal(
        <ImageCropModal
          open={state.open}
          imageSrc={state.imageSrc}
          spec={state.spec}
          loading={processing}
          onCancel={cancelCrop}
          onConfirm={confirmCrop}
        />,
        document.body
      )
    : null;

  return { openCrop, cropModal };
}
